/**
 * Profile tab — Personal Information + Financial Profile.
 *
 * Top 5 fields per section are always visible. "Show more" reveals
 * income exposures, services preferences, and other questionnaire data.
 *
 * The CustomerProfileResponse shape is currently augmented locally in
 * src/api/profile.ts; that intersection keeps consumers strongly typed
 * until `pnpm gen:types` is re-run.
 */
import * as React from 'react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@finmin/ui';
import {
  Briefcase,
  Calendar,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Mail,
  Phone,
  TrendingUp,
  User,
} from 'lucide-react';
import type { CustomerProfileResponse } from '@/api/profile';

interface Props {
  profile: CustomerProfileResponse | undefined;
  isLoading: boolean;
  fallbackMobile?: string | null;
}

const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  salaried: 'Salaried Professional',
  professional: 'Professional / Freelancer',
  business_owner: 'Business Owner',
  others: 'Other',
  self_employed: 'Professional / Freelancer',
};

const INCOME_RANGE_LABELS: Record<string, string> = {
  below_25l: 'Below ₹25 Lakh',
  '25_50l': '₹25L – ₹50L',
  '50l_2cr': '₹50L – ₹2 Cr',
  above_2cr: 'Above ₹2 Cr',
  below_20l: 'Below ₹20 Lakh',
  '20_75l': '₹20L – ₹75L',
  '75l_2cr': '₹75L – ₹2 Cr',
};

const TURNOVER_LABELS: Record<string, string> = {
  below_2cr: 'Below ₹2 Cr',
  '2_10cr': '₹2 Cr – ₹10 Cr',
  '10_50cr': '₹10 Cr – ₹50 Cr',
  above_50cr: 'Above ₹2 Cr',
};

const INCOME_EXPOSURE_LABELS: Record<string, string> = {
  esop_rsu: 'ESOPs / RSUs',
  capital_gains: 'Capital Gains',
  foreign_income_assets: 'Foreign Income / Assets',
  any_other: 'Any Other',
  none: 'None of the above',
};

const SERVICES_REQUIRED_LABELS: Record<string, string> = {
  bookkeeping_pl_management: 'Bookkeeping & P&L Management',
  compliance_related: 'Compliance',
  registrations: 'Registrations',
  compliance_filing: 'Compliance Filing',
  bookkeeping_advance_tax: 'Bookkeeping & Advance Tax',
  portfolio_planning: 'Portfolio Planning',
  itr_filing: 'ITR Filing',
  advance_tax_planning: 'Advance Tax Planning',
  huf_registration_planning: 'HUF Registration / Planning',
  portfolio_tracking: 'Portfolio Tracking',
  tax_planning: 'Tax Planning',
  epf_withdrawal: 'EPF Withdrawal',
  any_other: 'Any Other',
  none: 'None',
};

function labelOf(map: Record<string, string>, key: string): string {
  return map[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function ProfileTab({ profile, isLoading, fallbackMobile }: Props) {
  const [expanded, setExpanded] = React.useState(false);

  const hasMoreFinancial =
    Boolean(profile?.annual_turnover_range) ||
    Boolean(profile?.entity_structure) ||
    (profile?.income_exposures?.length ?? 0) > 0 ||
    (profile?.services_required?.length ?? 0) > 0 ||
    Boolean(profile?.other_customer_category);

  return (
    <div className="space-y-5">
      {/* ── Personal Information ─────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="text-muted-foreground h-4 w-4" aria-hidden />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <FieldsSkeleton />
          ) : (
            <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
              <ProfileField
                icon={<User className="h-4 w-4" />}
                label="Full name"
                value={profile?.full_name}
              />
              <ProfileField
                icon={<Phone className="h-4 w-4" />}
                label="Mobile number"
                value={profile?.mobile_e164 ?? fallbackMobile ?? undefined}
              />
              <ProfileField
                icon={<Mail className="h-4 w-4" />}
                label="Email address"
                value={profile?.email}
              />
              <ProfileField
                icon={<Calendar className="h-4 w-4" />}
                label="Date of birth"
                value={formatDate(profile?.dob)}
              />
            </dl>
          )}
        </CardContent>
      </Card>

      {/* ── Financial Profile ────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="text-muted-foreground h-4 w-4" aria-hidden />
            Financial Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <FieldsSkeleton />
          ) : (
            <div className="space-y-6">
              <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
                <ProfileField
                  icon={<Briefcase className="h-4 w-4" />}
                  label="Customer type"
                  value={
                    profile?.customer_type
                      ? labelOf(CUSTOMER_TYPE_LABELS, profile.customer_type)
                      : undefined
                  }
                />
                <ProfileField
                  icon={<DollarSign className="h-4 w-4" />}
                  label="Annual income range"
                  value={
                    profile?.annual_income_range
                      ? labelOf(INCOME_RANGE_LABELS, profile.annual_income_range)
                      : undefined
                  }
                />
              </dl>

              {expanded && (
                <div className="border-border space-y-5 border-t pt-5">
                  {profile?.annual_turnover_range && (
                    <DetailRow
                      label="Annual turnover"
                      value={labelOf(TURNOVER_LABELS, profile.annual_turnover_range)}
                    />
                  )}
                  {profile?.entity_structure && (
                    <DetailRow
                      label="Entity structure"
                      value={
                        <>
                          <span className="capitalize">
                            {profile.entity_structure.replace(/_/g, ' ')}
                          </span>
                          {profile.entity_structure_other_detail && (
                            <span className="text-muted-foreground">
                              {' '}
                              — {profile.entity_structure_other_detail}
                            </span>
                          )}
                        </>
                      }
                    />
                  )}
                  {profile?.income_exposures && profile.income_exposures.length > 0 && (
                    <BadgeRow
                      label="Income exposures"
                      items={profile.income_exposures.map((e) =>
                        labelOf(INCOME_EXPOSURE_LABELS, e),
                      )}
                    />
                  )}
                  {profile?.services_required && profile.services_required.length > 0 && (
                    <BadgeRow
                      label="Services you need"
                      items={profile.services_required.map((s) =>
                        labelOf(SERVICES_REQUIRED_LABELS, s),
                      )}
                    />
                  )}
                  {profile?.other_customer_category && (
                    <DetailRow
                      label="Category"
                      value={
                        <>
                          <span className="capitalize">
                            {profile.other_customer_category.replace(/_/g, ' ')}
                          </span>
                          {profile.other_customer_category_detail && (
                            <span className="text-muted-foreground">
                              {' '}
                              — {profile.other_customer_category_detail}
                            </span>
                          )}
                        </>
                      }
                    />
                  )}
                </div>
              )}

              {!profile?.customer_type && !profile?.annual_income_range && (
                <p className="text-muted-foreground py-2 text-sm">
                  Financial details not yet provided.
                </p>
              )}

              {hasMoreFinancial && (
                <div className="flex justify-center pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpanded((v) => !v)}
                    className="gap-1.5 text-xs"
                  >
                    {expanded ? (
                      <>
                        Show less
                        <ChevronUp className="h-3 w-3" aria-hidden />
                      </>
                    ) : (
                      <>
                        Show more
                        <ChevronDown className="h-3 w-3" aria-hidden />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ProfileField({
  icon,
  label,
  value,
  mono = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-muted-foreground mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
          {label}
        </dt>
        <dd
          className={`text-foreground mt-0.5 break-words text-sm ${
            mono ? 'font-mono tracking-wider' : 'font-medium'
          }`}
        >
          {value ?? <span className="text-muted-foreground font-normal">—</span>}
        </dd>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wide">
        {label}
      </dt>
      <dd className="text-foreground text-sm font-medium">{value}</dd>
    </div>
  );
}

function BadgeRow({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <dt className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide">
        {label}
      </dt>
      <dd className="flex flex-wrap gap-2">
        {items.map((it) => (
          <Badge key={it} variant="secondary">
            {it}
          </Badge>
        ))}
      </dd>
    </div>
  );
}

function FieldsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton className="mt-0.5 h-4 w-4 rounded" />
          <div className="flex-1">
            <Skeleton className="mb-1.5 h-3 w-20" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      ))}
    </div>
  );
}
